import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, FormControlLabel, Grid, IconButton, Radio, RadioGroup, TextField, Tooltip, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { clearBillingPRAInformation, getBillingInformationPTRA } from '../../store/formSlice';
import { useAppDispatch } from '../../store/store';
import RequiredLabel from '../UtilityComponent/RequiredLabel';
import { processPurchaseOrderNumbers, validateAndProcessInputValue } from '../UtilityComponent/validationUtils'; // Adjust the import path as needed

const programs = [
  { name: 'Pepsi', description: 'This will be for order that have a PO starting with 4500 and are 9 digits and delivering int Arlington TX, Grand Prairie TX or Carlisle PA.If you  do not select  for Arlington or Grand Prairie, it will be for a pepsi plant, and you will need the following PEPLOC/COST CENTER/ACCOUNT NUMBER.' },
  { name: 'Frito-Lay', description: 'This will be for orders picking up for plant, delivering or product for Frito-Lay production. Requires DivLoc/Account Number.You may also have a Frito-lay PO required examples(10-digit PO starting with a 45 or additional) FLNACREATIVE orders will have DIVLOC of 3700 with account number.' },
  { name: 'Gatorade/Quaker', description: 'Quaker RND or Quaker Misc. product you will have a 6 -digit cost center with a 2 PCNA RND or PNCA Misc. You will have a 6-digit cost center starting with a 3.' },
  { name: 'DFA', description: "Dairy Farmer America/NACP This will be a 10- digit PO starting with 4503. NACP doesn't have a PO 10 -digit po starting with a 453 then they just use a CC. for some of the  NACP orders delivering into Riverside CA they typically use a 5- digit PO starting with a 9" }
];

interface BillingInformationPTRAProps {
  onNext: () => void;
  onBack: () => void;
  values: {
    programInfo: string;
    researchAndDevelopmentFlag: string;
    arlingtonOrGrandPrairieLocationFlag: string;
    pepLoc: string;
    accountProjectNumber: string;
    costCenter: string;
    divLoc: string;
    purchaseOrderNumber: string;
    pickupNumber: string;
    additionalPurchaseOrder: string;
    purchaseOrderNumberList?: string[];
    companyCode: string;
    profitCenter: string;
  };
  errors: {
    errorMessage?: string;
    programInfo?: string;
    researchAndDevelopmentFlag?: string;
    arlingtonOrGrandPrairieLocationFlag?: string;
    pepLoc?: string;
    accountProjectNumber?: string;
    costCenter?: string;
    divLoc?: string;
    purchaseOrderNumber?: string;
    pickupNumber?: string;
    additionalPurchaseOrder?: string;
    companyCode: string;
    profitCenter: string;
  };
  touched: {
    programInfo?: string;
    researchAndDevelopmentFlag?: string;
    arlingtonOrGrandPrairieLocationFlag?: string;
    pepLoc?: string;
    accountProjectNumber?: string;
    costCenter?: string;
    divLoc?: string;
    purchaseOrderNumber?: boolean;
    pickupNumber?: boolean;
    additionalPurchaseOrder?: boolean;
    companyCode: string;
    profitCenter: string;
  };
  requestType: string;

}

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '80%',
    margin: 'auto',
    marginTop: '10px',
  },
  title: {
    color: 'blue',
    fontSize: '1rem'
  },
  radioGroup: {
    display: 'flex',
    justifyContent: 'space-around',
    width: '100%',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  radioInput: {
    marginRight: '8px',
  },
  questionContainer: {
    marginTop: '10px',
    textAlign: 'center',
  },
  additionalFieldsContainer: {
    marginTop: '20px',
    width: '100%',
    marginBottom: '30px',
    paddingBottom: '5rem'
  },
  fieldGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
    width: '100%',
    marginTop: '20px'
  },
  inputContainer: {
    flex: 1,
    marginRight: '10px',
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: '5px',
  },
  input: {
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    width: '100%', // Increase the width to 100%
  },
  or: {
    alignSelf: 'center',
    margin: '0 10px',
    fontWeight: 'bold',
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: 'blue',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  note: {
    marginTop: '5px',
    fontSize: '12px',
    color: '#777',
  },
  customNote: {
    marginTop: '5px',
    fontSize: '12px',
    color: '#777',
    /* marginBottom: '5rem' */
  },
  customNotePep: {
    fontSize: '12px',
    color: 'red',
    /* marginBottom: '5rem' */
  },
  inputRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '15px',
    paddingBottom: '3rem'
  },
  ulCls: {
    listStyle: 'none',
    display: 'flex',
    padding: '4px',
    margin: '0px',
    flexWrap: 'wrap'
  },
  liCls: {
    paddingTop: '5px',
    paddingBottom: '5px'
  },
  gridSpacing: {
    marginBottom: '2rem', // Increase the space between the grids
  },
};

const BillingInformationPTRA: React.FC<BillingInformationPTRAProps> = ({
  onNext,
  onBack,
  values,
  errors,
  touched,
  requestType,
}) => {
  const { setFieldValue, setFieldTouched, setErrors, setFieldError } = useFormikContext<any>();

  const billingInfoObj = useSelector(getBillingInformationPTRA);
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(null);
  const [purchaseOrderError, setPurchaseOrderError] = useState<string | null>(null);
  const [costCenterError, setCostCenterError] = useState<string | null>(null);
  const [accountProjectError, setAccountProjectError] = useState<string | null>(null);

  const [inputValue, setInputValue] = useState<string>('');
  const { programInfo, researchAndDevelopmentFlag,
    arlingtonOrGrandPrairieLocationFlag,
    pepLoc, accountProjectNumber, costCenter, divLoc, purchaseOrderNumber, pickupNumber, purchaseOrderNumberList, companyCode, profitCenter
  } = useSelector(getBillingInformationPTRA)
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();


  useEffect(() => {
    console.log("inside heerere")
    dispatch(clearBillingPRAInformation());
    if (programInfo === 'Pepsi') {
      if (programInfo !== undefined && !values.programInfo) {
        setFieldValue('programInfo', programInfo);
      }
      // if (researchAndDevelopmentFlag !== undefined && !values.researchAndDevelopmentFlag) {
      //   setFieldValue('researchAndDevelopmentFlag', researchAndDevelopmentFlag ? 'Yes' : 'No');
      // }
      if (arlingtonOrGrandPrairieLocationFlag !== undefined && !values.arlingtonOrGrandPrairieLocationFlag) {
        setFieldValue('arlingtonOrGrandPrairieLocationFlag', arlingtonOrGrandPrairieLocationFlag === null ? '' : arlingtonOrGrandPrairieLocationFlag ? 'Yes' : 'No');
      }
      if (arlingtonOrGrandPrairieLocationFlag === 'Yes') {
        if (purchaseOrderNumber !== undefined && !values.purchaseOrderNumber) {
          setFieldValue('purchaseOrderNumber', purchaseOrderNumber);
        }
        if (pickupNumber !== undefined && !values.pickupNumber) {
          setFieldValue('pickupNumber', pickupNumber);
        }
        if (companyCode !== undefined && !values.companyCode) {
          setFieldValue('companyCode', companyCode);
        }
        if (profitCenter !== undefined && !values.profitCenter) {
          setFieldValue('profitCenter', profitCenter);
        }
        if (costCenter !== undefined && !values.costCenter) {
          setFieldValue('costCenter', costCenter);
        }
      } else {
        if (researchAndDevelopmentFlag) {
          if (pepLoc !== undefined && !values.pepLoc) {
            setFieldValue('pepLoc', pepLoc);
          }
          if (accountProjectNumber !== undefined && !values.accountProjectNumber) {
            setFieldValue('accountProjectNumber', accountProjectNumber);
          }
          if (costCenter !== undefined && !values.costCenter) {
            setFieldValue('costCenter', costCenter);
          }
        } else {
          if (pepLoc !== undefined && !values.pepLoc) {
            setFieldValue('pepLoc', pepLoc);
          }
          if (accountProjectNumber !== undefined && !values.accountProjectNumber) {
            setFieldValue('accountProjectNumber', accountProjectNumber);
          }
          // if (costCenter !== undefined && !values.costCenter) {
          //   setFieldValue('costCenter', costCenter);
          // }
        }
      }
      if (divLoc !== undefined && !values.divLoc) {
        setFieldValue('divLoc', divLoc === null ? '' : divLoc);
      }

      if (purchaseOrderNumberList !== undefined && !values.purchaseOrderNumberList) {
        setFieldValue('purchaseOrderNumberList', purchaseOrderNumberList);
      }
      setFieldValue('purchaseOrderNumber', '');
      setFieldValue('pickupNumber', '');
    }

    if (programInfo === 'Frito-Lay') {
      if (programInfo !== undefined && !values.programInfo) {
        setFieldValue('programInfo', programInfo);
      }
      if (researchAndDevelopmentFlag !== undefined && !values.researchAndDevelopmentFlag) {
        setFieldValue('researchAndDevelopmentFlag', researchAndDevelopmentFlag ? 'Yes' : 'No');
      }
      setFieldValue('arlingtonOrGrandPrairieLocationFlag', '');
      setFieldValue('pepLoc', '');
      setFieldValue('accountProjectNumber', '');
      setFieldValue('costCenter', '');
      if (divLoc !== undefined && !values.divLoc) {
        setFieldValue('divLoc', divLoc === null ? '' : divLoc);
      }
      setFieldValue('purchaseOrderNumber', '');
      setFieldValue('pickupNumber', '');

      if (purchaseOrderNumberList !== undefined && !values.purchaseOrderNumberList) {
        setFieldValue('purchaseOrderNumberList', purchaseOrderNumberList);
      }
    }

    if (programInfo === 'Gatorade/Quaker' || programInfo === 'DFA') {
      if (programInfo !== undefined && !values.programInfo) {
        setFieldValue('programInfo', programInfo);
      }
      if (researchAndDevelopmentFlag !== undefined && !values.researchAndDevelopmentFlag) {
        setFieldValue('researchAndDevelopmentFlag', '');
      }
      setFieldValue('arlingtonOrGrandPrairieLocationFlag', '');
      setFieldValue('pepLoc', '');
      setFieldValue('accountProjectNumber', '');
      if (costCenter !== undefined && !values.costCenter) {
        setFieldValue('costCenter', costCenter);
      }
      if (divLoc !== undefined && !values.divLoc) {
        setFieldValue('divLoc', divLoc === null ? '' : divLoc);
      }
      if (programInfo === 'DFA') {
        setFieldValue('purchaseOrderNumber', '');
        setFieldValue('companyCode', '');
        setFieldValue('profitCenter', '');
      } else {
        if (purchaseOrderNumber !== undefined && !values.purchaseOrderNumber) {
          setFieldValue('purchaseOrderNumber', purchaseOrderNumber);
        }
        if (purchaseOrderNumberList !== undefined && !values.purchaseOrderNumberList) {
          setFieldValue('purchaseOrderNumberList', purchaseOrderNumberList);
        }
      }
      setFieldValue('pickupNumber', '');
    }
    console.log("inside heerere 12345")


  }, [
    setFieldValue,
    values.programInfo,
    values.researchAndDevelopmentFlag,
    values.arlingtonOrGrandPrairieLocationFlag,
    values.pepLoc,
    values.accountProjectNumber,
    values.costCenter,
    values.divLoc,
    values.purchaseOrderNumber,
    values.pickupNumber,
    values.purchaseOrderNumberList,
    values.companyCode,
    values.profitCenter
  ]);
  // ...existing code...

  // ...existing code...

  useEffect(() => {
    const purchaseOrderNumber = values.purchaseOrderNumber;
    if (purchaseOrderNumber) {
      processPurchaseOrderNumbers(purchaseOrderNumber, setFieldTouched, setFieldError, setFieldValue, setLocalErrorMessage);
    } else {
      setFieldValue('purchaseOrderNumberList', values.purchaseOrderNumberList);
      setFieldError('purchaseOrderNumber', ''); // Clear any existing error
    }
  }, [values.purchaseOrderNumber, values.purchaseOrderNumberList, setFieldValue, setFieldError, setFieldTouched]);



  const handleProgramChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newProgram = event.target.value;

    setFieldValue('programInfo', newProgram); // Update the values object
    setErrors({ ...errors, programInfo: '' }); // Clear the error message
    resetFields(); // Reset fields when program changes
  };

  const handleResearchDevelopmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newResearchDevelopment = event.target.value;
    setFieldValue('researchAndDevelopmentFlag', newResearchDevelopment); // Update the values object
    setErrors({ ...errors, programInfo: '' }); // Clear the error message
    setAccountProjectError('')
    resetFields(); // Reset fields when R&D response changes
  };

  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocation = event.target.value;

    setFieldValue('arlingtonOrGrandPrairieLocationFlag', newLocation); // Update the values object
    setErrors({ ...errors, programInfo: '' }); // Clear the error message
  };

  const handlePepsLocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPepsLoc = event.target.value;
    setFieldValue('pepLoc', newPepsLoc); // Update the values object
    setErrors({ ...errors, pepLoc: '' }); // Clear the error message
  };

  const handleAccountProjectNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAccountProjectNumber = event.target.value;
    setFieldValue('accountProjectNumber', newAccountProjectNumber); // Update the values object
    setErrors({ ...errors, accountProjectNumber: '' }); // Clear the error message
  };

  const handleCostCenterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCostCenter = event.target.value;
    setFieldValue('costCenter', newCostCenter); // Update the values object
    setErrors({ ...errors, costCenter: '' }); // Clear the error message
  };

  const handlePurchaseOrderNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPurchaseOrderNumber = event.target.value;
    setFieldValue('purchaseOrderNumber', newPurchaseOrderNumber); // Update the values object
    setErrors({ ...errors, purchaseOrderNumber: '' }); // Clear the error message
  };

  const handlePickupNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPickupNumber = event.target.value;
    setFieldValue('pickupNumber', newPickupNumber); // Update the values object
    setErrors({ ...errors, pickupNumber: '' }); // Clear the error message
  };

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && e.currentTarget.selectionStart === 0) {
      setFieldValue('purchaseOrderNumber', '');
      setInputValue('');
    }
  };

  const handleDeleteChip = (index: number) => {
    const updatedList = values.purchaseOrderNumberList?.filter((_, i) => i !== index) || [];
    setFieldValue('purchaseOrderNumberList', updatedList);
    setFieldValue('purchaseOrderNumber', updatedList.join('; '));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setLocalErrorMessage(null); // Reset error message
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputValue]);



  const resetFields = () => {
    setErrors({}); // Clear all errors
    setFieldValue('pepLoc', '');
    setFieldValue('accountProjectNumber', '');
    // setFieldValue('costCenter', '');
    setFieldValue('purchaseOrderNumber', '');
    setFieldValue('pickupNumber', '');
    setFieldValue('divLoc', '');
    /* setFieldValue('researchAndDevelopmentFlag', '');
    setFieldValue('arlingtonOrGrandPrairieLocationFlag', ''); */
    setFieldValue('companyCode', '');
    setFieldValue('profitCenter', '');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault(); // Prevent default behavior for Enter and Tab keys
      validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);
      setInputValue(''); // Clear the input value after processing
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);
      setInputValue(''); // Clear the input value after processing
    }
  };

  // Clear chips when programInfo, researchAndDevelopmentFlag, or arlingtonOrGrandPrairieLocationFlag changes
  useEffect(() => {
    console.log("inside usfee 467")
    setFieldValue('purchaseOrderNumber', '');
    setFieldValue('pepLoc', '');
    setFieldValue('accountProjectNumber', '');
    // setFieldValue('costCenter', '');
    setFieldValue('purchaseOrderNumber', '');
    setFieldValue('pickupNumber', '');
    setFieldValue('divLoc', '');
    /* setFieldValue('researchAndDevelopmentFlag', '');
    setFieldValue('arlingtonOrGrandPrairieLocationFlag', ''); */
    setFieldValue('companyCode', '');
    setFieldValue('profitCenter', '');
    // setFieldValue('purchaseOrderNumberList', []);

    setFieldTouched('pepLoc', false);
    setFieldTouched('accountProjectNumber', false);
    // setFieldTouched('costCenter', false);
    setFieldTouched('divLoc', false);
    setFieldTouched('purchaseOrderNumber', false);
    setFieldTouched('pickupNumber', false);
    setFieldTouched('profitCenter', false);
    setFieldTouched('companyCode', false);
    setLocalErrorMessage(null);
  }, [values.programInfo, values.researchAndDevelopmentFlag, values.arlingtonOrGrandPrairieLocationFlag, setFieldValue]);

  useEffect(() => {
    setErrors({})
  }, [values.programInfo, values.researchAndDevelopmentFlag, values.arlingtonOrGrandPrairieLocationFlag]);

  const handleNext = () => {
    validateAndProcessInputValue(inputValue, values, setFieldValue, setLocalErrorMessage, requestType);

    if ((values.programInfo === 'Gatorade/Quaker') && values.purchaseOrderNumber.startsWith('41')) {
      if (!values.costCenter.startsWith('2') && !values.costCenter.startsWith('3')) {
        setCostCenterError('Cost Center should start with 2 or 3 when Purchase Order Number begins with 41.');
        return;
      }
    }

    if (values.programInfo === 'Frito-Lay' && values.researchAndDevelopmentFlag === 'Yes' && !values.accountProjectNumber.startsWith('RD')) {
      setAccountProjectError('Account number/Project number should begin with "RD" for Frito-Lay.');
      return;
    }
    onNext();
  };


  const handleFritoLayAccountProjectNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAccountProjectNumber = event.target.value;
    if (values.programInfo === 'Frito-Lay') {
      if (!newAccountProjectNumber.startsWith('RD') && values.researchAndDevelopmentFlag === 'Yes') {
        setAccountProjectError('Account number/Project number should begin with "RD"');
      } else {
        setAccountProjectError(null); // Clear the error message if validation passes
      }
    }
    setFieldValue('accountProjectNumber', newAccountProjectNumber);
    setErrors({ ...errors, accountProjectNumber: '' }); // Clear the error message
  };

  return (
    <div style={{ height: 'auto' }}>
      <div style={styles.container}>
        <h2 style={styles.title}>Select one of the Programs</h2>
        <div>
          <RadioGroup row value={values.programInfo} onChange={handleProgramChange}
            sx={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            {programs.map((program) => (
              <div key={program.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '150px', marginRight: '1rem', flexGrow: 1 }}>
                <FormControlLabel
                  value={program.name}
                  control={<Radio />}
                  label={program.name}
                  style={{ marginRight: '10px' }}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }}
                />
                {!values.programInfo && (
                  <Box
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '8px',
                      marginTop: '4px',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      width: '100%',
                      flexGrow: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      color: '#0047ba'
                    }}
                  >
                    {program.description}
                  </Box>
                )}
              </div>
            ))}
          </RadioGroup>
        </div>
        <ErrorMessage name="programInfo" component="div" className="error-message" />

        {(values.programInfo === 'Frito-Lay') && (
          <div style={styles.questionContainer}>
            <h2 style={styles.title}>Is this a Research and Development order ?</h2>
            <div>
              <RadioGroup row value={values.researchAndDevelopmentFlag} onChange={handleResearchDevelopmentChange} sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" style={{ marginRight: '30px', marginLeft: '50px' }} sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                <FormControlLabel value="No" control={<Radio />} label="No" style={{ marginRight: '30px' }} sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
              </RadioGroup>
            </div>

            <ErrorMessage name="researchAndDevelopmentFlag" component="div" className="error-message" />
          </div>
        )}

        {(values.programInfo === 'Pepsi') && (
          <div style={styles.questionContainer}>
            <h2 style={styles.title}>Is this order coming from or going to Arlington or Grand Prairie location ?</h2>
            <div>
              <RadioGroup row value={values.arlingtonOrGrandPrairieLocationFlag} onChange={handleLocationChange} sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" style={{ marginRight: '30px', marginLeft: '9.9rem' }} sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                <FormControlLabel value="No" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' }, marginRight: '0px' }} />
                <Tooltip title="Plant to Plant or any product delivering to a Pepsi plant">
                  <IconButton sx={{
                    marginLeft: '-10px !important', marginTop: '11px', height: '20px', width: '20px',
                    '&:hover': { background: 'none' }
                  }} size='small'>
                    <HelpOutlineIcon sx={{ height: '20px', width: '20px' }} />
                  </IconButton>
                </Tooltip>
              </RadioGroup>
            </div>
            <ErrorMessage name="arlingtonOrGrandPrairieLocationFlag" component="div" className="error-message" />
          </div>
        )}


        {(values.programInfo === 'Pepsi' && values.arlingtonOrGrandPrairieLocationFlag === 'No') && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <RequiredLabel label="PepLoc" />
                <TextField
                  id="pepLoc"
                  name="pepLoc"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pepLoc}
                  placeholder="Please enter PepLoc number"
                  onChange={handlePepsLocChange}
                />
                <ErrorMessage name="pepLoc" component="div" className="error-message" />
              </Grid>
              {/* <Grid item xs={12} sm={2}>
                <Typography
                  variant="body1"
                  sx={{
                    display: "flex",
                    alignItems: "center", // Center dots and text
                    justifyContent: "center",
                    fontSize: "14px", // Adjust font size if needed
                    whiteSpace: "nowrap",
                    marginBottom: '0px',
                    marginLeft: '5px',
                    marginRight: '10px',
                    marginTop: '35px',
                    color: '#616161'
                  }}
                >
                  <span style={{ margin: "0 4px" }}>•••••</span>or<span style={{ margin: "0 4px" }}>•••••</span>
                </Typography>
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <RequiredLabel label="Account Number/Project Number" />
                <TextField
                  id="accountProjectNumber"
                  name="accountProjectNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.accountProjectNumber}
                  placeholder="Please enter Account number or Project number"
                  onChange={handleAccountProjectNumberChange}
                />
                <ErrorMessage name="accountProjectNumber" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Cost Center" mandatory={true} />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={handleCostCenterChange}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Purchase Order or GBILL" mandatory={true} />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {localErrorMessage && (
                    <div className="error-message">{localErrorMessage}</div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={handlePickupNumberChange}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
                <p style={styles.customNote}>* If No Pickup Number Exists, Please Put in PO Number</p>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
          </div>
        )}

        {(values.programInfo === 'Pepsi' && values.arlingtonOrGrandPrairieLocationFlag === 'Yes') && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <RequiredLabel label="Purchase Order Number" mandatory={!values.costCenter} />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                {values.purchaseOrderNumberList && values.purchaseOrderNumberList?.length > 0 && <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>
                }
                {((touched.purchaseOrderNumber && errors.purchaseOrderNumber) || localErrorMessage) && <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {localErrorMessage && (
                    <div className="error-message">{localErrorMessage}</div>
                  )}
                </div>
                }
                <small style={styles.customNotePep}>
                  *Purchase Order number should be a 1-10 digit code starting with 4500
                </small>
              </Grid>
              <Grid item xs={12} sm={6}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={handlePickupNumberChange}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Cost Center" mandatory={values.purchaseOrderNumberList?.length === 0} />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={(e) => setFieldValue('costCenter', e.target.value)}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
          </div>
        )}

        {values.programInfo === 'Frito-Lay' && values.researchAndDevelopmentFlag && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="DivLoc" mandatory={true} />
                <TextField
                  id="divLoc"
                  name="divLoc"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.divLoc}
                  placeholder="Please enter DivLoc number"
                  onChange={(e) => setFieldValue('divLoc', e.target.value)}
                />
                <ErrorMessage name="divLoc" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Account Number/Project Number" />
                <TextField
                  id="accountProjectNumber"
                  name="accountProjectNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.accountProjectNumber}
                  placeholder="Please enter Account number/Project number"
                  onChange={handleFritoLayAccountProjectNumberChange}
                />


                {values.programInfo === 'Frito-Lay' && accountProjectError && (
                  <div className="error-message">{accountProjectError}</div>
                )}

                {values.programInfo === 'Frito-Lay' && values.researchAndDevelopmentFlag === 'Yes' && (
                  <small style={styles.customNote}>
                    *Account number/Project number should begin with 'RD'
                  </small>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Purchase Order Number" mandatory={true} />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {localErrorMessage && (
                    <div className="error-message">{localErrorMessage}</div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={(e) => setFieldValue('pickupNumber', e.target.value)}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
                <p style={styles.customNote}>* If No Pickup Number Exists, Please Put in PO Number</p>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Cost Center" />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={handleCostCenterChange}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
          </div>
        )}


        {values.programInfo === 'Gatorade/Quaker' && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Purchase Order Number" />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {/* {localErrorMessage && (
                  <div className="error-message">{localErrorMessage}</div>
                )} */}
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>

                <RequiredLabel label="Cost Center" mandatory={true} />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={(e) => {
                    setFieldValue('costCenter', e.target.value);
                    setLocalErrorMessage(null); // Clear the error message if validation passes
                  }}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
                {costCenterError && (
                  <div className="error-message">{costCenterError}</div>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={(e) => setFieldValue('pickupNumber', e.target.value)}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
                <p style={styles.customNote}>* If no Pickup Number Exists, Please Put in PO Number</p>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
          </div>
        )}

        {values.programInfo === 'Quaker' && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Purchase Order Number" mandatory={true} />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {/*  {localErrorMessage && (
                  <div className="error-message">{localErrorMessage}</div>
                )} */}
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>

                <RequiredLabel label="Cost Center" mandatory={true} />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={(e) => {
                    setFieldValue('costCenter', e.target.value);
                    setLocalErrorMessage(null); // Clear the error message if validation passes
                  }}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
                {costCenterError && (
                  <div className="error-message">{costCenterError}</div>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={(e) => setFieldValue('pickupNumber', e.target.value)}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
                <p style={styles.customNote}>* If no Pickup Number Exists, Please Put in PO Number</p>
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
          </div>
        )}

        {values.programInfo === 'DFA' && (
          <div style={styles.additionalFieldsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <RequiredLabel label="Purchase Order Number" mandatory={true} />
                <TextField
                  id="purchaseOrderNumber"
                  name="purchaseOrderNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder="Please enter Purchase order or GBILL number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={handleBackspace}
                />
                <div className="chips-container">
                  <ul style={styles.ulCls}>
                    {values.purchaseOrderNumberList && values.purchaseOrderNumberList.map((po, index) => (
                      <li style={styles.liCls} key={index}>
                        <Chip label={po} onDelete={() => handleDeleteChip(index)} style={{ marginRight: '3px' }} />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="error-container">
                  {touched.purchaseOrderNumber && errors.purchaseOrderNumber && (
                    <div className="error-message">{errors.purchaseOrderNumber}</div>
                  )}
                  {localErrorMessage && (
                    <div className="error-message">{localErrorMessage}</div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography
                  variant="body1"
                  sx={{
                    display: "flex",
                    alignItems: "center", // Center dots and text
                    justifyContent: "center",
                    fontSize: "14px", // Adjust font size if needed
                    whiteSpace: "nowrap",
                    marginBottom: '0px',
                    marginLeft: '5px',
                    marginRight: '10px',
                    marginTop: '35px',
                    color: '#616161',
                  }}
                >
                  <span style={{ margin: "0 4px" }}>•••••</span>or<span style={{ margin: "0 4px" }}>•••••</span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <RequiredLabel label="Cost Center" />
                <TextField
                  id="costCenter"
                  name="costCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.costCenter}
                  placeholder="Please enter Cost Center number"
                  onChange={(e) => setFieldValue('costCenter', e.target.value)}
                />
                <ErrorMessage name="costCenter" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Pickup Number" />
                <TextField
                  id="pickupNumber"
                  name="pickupNumber"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.pickupNumber}
                  placeholder="Please enter Pickup number"
                  onChange={(e) => setFieldValue('pickupNumber', e.target.value)}
                />
                <ErrorMessage name="pickupNumber" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField
                  id="companyCode"
                  name="companyCode"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.companyCode}
                  placeholder="Please enter Company Code"
                  onChange={(e) => setFieldValue('companyCode', e.target.value)}
                />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Profit Center" />
                <TextField
                  id="profitCenter"
                  name="profitCenter"
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: {
                      fontSize: '0.8rem',
                    },
                  }}
                  autoComplete="off"
                  value={values.profitCenter}
                  placeholder="Please enter Profit Center"
                  onChange={(e) => setFieldValue('profitCenter', e.target.value)}
                />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </Grid>
            </Grid>
            <p style={styles.customNote}>* If No Pickup Number Exists, Please Put in PO Number</p>
          </div>

        )}

        {/*  {(values.programInfo === 'Gatorade' || values.programInfo === 'Quaker' || values.programInfo === 'DFA') &&
          <div style={styles.additionalFieldsContainer}>
            <div style={styles.fieldGroup}>
              <div style={styles.inputContainer}>
                <RequiredLabel label="Company Code" mandatory={false} />
                <TextField id="companyCode" name="companyCode" type={"text"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.companyCode} placeholder="Please enter Company Code" onChange={(e) => setFieldValue('divLoc', e.target.value)} />
                <ErrorMessage name="companyCode" component="div" className="error-message" />
              </div>
              <div style={styles.inputContainer}>
                <RequiredLabel label="Profit Center" />
                <TextField id="profitCenter" name="profitCenter" type={"text"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.profitCenter} placeholder="Please enter Profit Center" onChange={(e) => setFieldValue('accountProjectNumber', e.target.value)} />
                <ErrorMessage name="profitCenter" component="div" className="error-message" />
              </div>
            </div>
          </div>
        } */}

      </div>

      <Field name="requestType" type="hidden" value={requestType} />

      <Box
        sx={{
          position: 'fixed', // Keep the buttons fixed at the bottom
          bottom: '60px', // Distance from the bottom of the screen
          left: '8rem', // Maintain position on normal screens
          width: 'calc(100% - 16rem)', // Adjust width dynamically based on left margin
          display: 'flex',
          flexDirection: 'row', // Keep buttons in a row
          justifyContent: 'space-between', // Align Back to the left and Next to the right
          alignItems: 'center',
          padding: '0', // Remove extra padding
          gap: '0', // Remove gaps between buttons
          '@media (max-width: 768px)': {
            left: '1rem', // Adjust left margin for smaller screens
            width: 'calc(100% - 2rem)', // Adjust width for smaller screens
          },
        }}
      >
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={onNext}
        >
          Next
        </Button>
      </Box>
    </div>
  );
};

export default BillingInformationPTRA;



